/// <reference lib="dom" />
// Screen only (the package's tsconfig has no DOM; this file adds it).
//
// When someone else's change (another device, an agent) lands in the body, what
// the person is looking at does not move on screen.
//
// Around the transaction applying a remote change, the vertical position of a
// marker is measured and the scroll is moved back by the difference. The marker:
//   - the caret, when the body has focus and the caret is visible (where one
//     is writing does not move);
//   - otherwise the first visible block (its start);
//   - the bottom of the body, when the body has scrolled up out of view (one is
//     reading a field below);
//   - nothing when the body is entirely below the view (nothing visible moves).
// "Before" is measured in `filterTransaction` (old DOM), "after" in the view's
// `update` (new DOM). Scroll anchoring (overflow-anchor) is off meanwhile so
// the browser does not correct twice.
//
// Which changes are remote is the caller's decision (Yjs sync for documents,
// a mark of the field's own for fields without Yjs).

import { Extension } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

/** The nearest vertically scrolling ancestor (or the document). */
function scrollParent(el: HTMLElement): HTMLElement {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    if (overflowY === "auto" || overflowY === "scroll") return p;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.body;
}

/** The visible vertical range of a scroller (screen coordinates). */
function visibleBox(scroller: HTMLElement) {
  if (scroller === document.scrollingElement)
    return { top: 0, bottom: window.innerHeight };
  const r = scroller.getBoundingClientRect();
  return { top: r.top, bottom: r.bottom };
}

/** Measures the marker (`after`: after the transaction). Null: nothing to keep. */
type Measure = (after: boolean) => number;

function anchor(
  view: EditorView,
  tr: Transaction,
  scroller: HTMLElement,
): Measure | null {
  const box = visibleBox(scroller);
  const field = view.dom.getBoundingClientRect();
  if (field.bottom <= box.top)
    return () => view.dom.getBoundingClientRect().bottom;
  if (field.top >= box.bottom) return null;
  const head = view.state.selection.head;
  if (view.hasFocus()) {
    const caret = view.coordsAtPos(head);
    if (caret.top >= box.top && caret.bottom <= box.bottom)
      return (after) =>
        view.coordsAtPos(after ? view.state.selection.head : head).top;
  }
  const top = Math.max(box.top, field.top);
  for (const child of Array.from(view.dom.children) as HTMLElement[]) {
    if (child.getBoundingClientRect().bottom <= top) continue;
    // The marker is the start of that block. Yjs sync arrives as a transaction
    // replacing the whole body (its mapping moves everything to the start), and
    // ProseMirror may reuse an element for another block's content, so neither
    // is trusted: the range that really changed is found by comparing the
    // documents, and the position is mapped around it.
    const pos = view.posAtDOM(child, 0);
    const before = view.state.doc;
    return (after) =>
      view.coordsAtPos(after ? mapThroughChange(before, tr.doc, pos) : pos).top;
  }
  return null;
}

/**
 * Maps `pos` of `before` into `after`: before the changed range it stays,
 * after it shifts by the length difference, inside it goes to the range start.
 */
function mapThroughChange(before: PMNode, after: PMNode, pos: number) {
  const start = before.content.findDiffStart(after.content);
  if (start === null || pos < start) return pos;
  const end = before.content.findDiffEnd(after.content);
  if (!end) return pos;
  let { a: endA, b: endB } = end;
  // With repetition (runs of line breaks) the match from the end passes
  // `start`; move it back by the overlap.
  const overlap = start - Math.min(endA, endB);
  if (overlap > 0) {
    endA += overlap;
    endB += overlap;
  }
  if (pos >= endA) return pos + (endB - endA);
  return Math.min(start, after.content.size);
}

/** Fields of the same scroller that turned anchoring off (the last one restores it). */
const anchorOff = new WeakMap<HTMLElement, { count: number; prev: string }>();

function suspendAnchoring(scroller: HTMLElement) {
  const off = anchorOff.get(scroller) ?? {
    count: 0,
    prev: scroller.style.overflowAnchor,
  };
  off.count++;
  anchorOff.set(scroller, off);
  scroller.style.overflowAnchor = "none";
  requestAnimationFrame(() => {
    if (--off.count > 0) return;
    anchorOff.delete(scroller);
    scroller.style.overflowAnchor = off.prev;
  });
}

export function createKeepViewPlugin(isRemote: (tr: Transaction) => boolean) {
  let current: EditorView | null = null;
  let pending: {
    scroller: HTMLElement;
    measure: Measure;
    before: number;
  } | null = null;
  return new Plugin({
    key: new PluginKey("trameriaKeepView"),
    filterTransaction(tr, state) {
      const view = current;
      if (!view || !tr.docChanged || !isRemote(tr)) return true;
      // The first content arriving in an empty body (sync right after opening).
      if (state.doc.textContent === "" && state.doc.childCount <= 1)
        return true;
      const scroller = scrollParent(view.dom);
      // Not drawn (a closed field, a test DOM).
      if (scroller.clientHeight === 0) return true;
      const measure = anchor(view, tr, scroller);
      if (!measure) return true;
      suspendAnchoring(scroller);
      pending = { scroller, measure, before: measure(false) };
      return true;
    },
    view(view) {
      current = view;
      return {
        update() {
          const p = pending;
          pending = null;
          if (!p) return;
          const shift = p.measure(true) - p.before;
          if (shift !== 0) p.scroller.scrollTop += shift;
        },
        destroy() {
          current = null;
          pending = null;
        },
      };
    },
  });
}

/** {@link createKeepViewPlugin} as an extension; `isRemote` tells remote changes apart. */
export const KeepViewOnRemote = Extension.create<{
  isRemote: (tr: Transaction) => boolean;
}>({
  name: "keepViewOnRemote",
  addOptions() {
    return { isRemote: () => false };
  },
  addProseMirrorPlugins() {
    return [createKeepViewPlugin(this.options.isRemote)];
  },
});
